(() => {
  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wrap = (value, length) => ((value % length) + length) % length;

  function initialize(scope = document) {
    scope.querySelectorAll('[data-pv-review-carousel]').forEach((root) => {
      if (instances.has(root)) return;
      const track = root.querySelector('[data-pv-review-track]');
      const controls = root.querySelector('[data-pv-review-controls]');
      const previous = root.querySelector('[data-pv-review-prev]');
      const next = root.querySelector('[data-pv-review-next]');
      const position = root.querySelector('[data-pv-review-position]');
      const cards = Array.from(track?.children || []).filter((card) => !card.hasAttribute('data-pv-review-copy'));
      if (!track || !controls || !previous || !next || !position || cards.length < 2) return;

      const controller = new AbortController();
      const { signal } = controller;
      let frame = 0;
      let settleTimer = 0;
      let pointerDown = false;
      let step = 0;
      let span = 0;
      let logicalPosition = 0;
      let visible = 1;
      const copies = [];

      function duplicate(card) {
        const copy = card.cloneNode(true);
        copy.setAttribute('data-pv-review-copy', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('inert', '');
        copy.removeAttribute('role');
        [copy, ...copy.querySelectorAll('*')].forEach((node) => {
          node.removeAttribute('id');
          node.removeAttribute('data-shopify-editor-block');
        });
        copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
        copies.push(copy);
        return copy;
      }

      // Only original reviews are accessible. Decorative copies provide a
      // gesture buffer on both sides; the carousel never advances on its own.
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      cards.forEach((card) => before.append(duplicate(card)));
      cards.forEach((card) => after.append(duplicate(card)));
      track.prepend(before);
      track.append(after);

      function update() {
        frame = 0;
        if (!step) return;
        logicalPosition = wrap((track.scrollLeft - span) / step, cards.length);
        const first = wrap(Math.round(logicalPosition), cards.length) + 1;
        const last = wrap(first + visible - 2, cards.length) + 1;
        previous.disabled = false;
        next.disabled = false;
        controls.hidden = false;
        position.textContent = `${visible === 1 ? first : `${first}–${last}`} / ${cards.length}`;
      }

      function recenter() {
        if (!step || pointerDown) return;
        if (track.scrollLeft < span - 1 || track.scrollLeft >= span * 2 - 1) {
          track.scrollTo({ left: span + wrap(track.scrollLeft - span, span), behavior: 'instant' });
        }
        update();
      }

      function settle() {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(recenter, 180);
      }

      function onScroll() {
        if (!frame) frame = requestAnimationFrame(update);
        settle();
      }

      function resize() {
        const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
        const nextStep = cards[0].getBoundingClientRect().width + gap;
        if (nextStep <= gap) return;
        const nextVisible = Math.max(1, Math.floor((track.clientWidth + gap + 1) / nextStep));
        if (Math.abs(nextStep - step) > .1) {
          step = nextStep;
          span = step * cards.length;
          track.scrollTo({ left: span + logicalPosition * step, behavior: 'instant' });
        }
        visible = nextVisible;
        update();
      }

      function move(direction) {
        if (!step) return;
        recenter();
        const left = (Math.round(track.scrollLeft / step) + direction * visible) * step;
        track.scrollTo({ left, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
        settle();
      }

      previous.addEventListener('click', () => move(-1), { signal });
      next.addEventListener('click', () => move(1), { signal });
      track.addEventListener('scroll', onScroll, { passive: true, signal });
      track.addEventListener('scrollend', recenter, { passive: true, signal });
      track.addEventListener('pointerdown', () => { pointerDown = true; }, { passive: true, signal });
      const release = () => { pointerDown = false; settle(); };
      window.addEventListener('pointerup', release, { passive: true, signal });
      window.addEventListener('pointercancel', release, { passive: true, signal });
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          move(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          track.scrollTo({ left: span + (event.key === 'Home' ? 0 : cards.length - 1) * step, behavior: 'instant' });
        }
      }, { signal });
      root.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('.pv-reviews__card');
        const index = cards.indexOf(card);
        if (index < 0) return;
        track.scrollTo({ left: span + index * step, behavior: 'instant' });
      }, { signal });

      const observer = new ResizeObserver(resize);
      observer.observe(track);
      instances.set(root, () => {
        controller.abort();
        observer.disconnect();
        window.clearTimeout(settleTimer);
        if (frame) cancelAnimationFrame(frame);
        copies.forEach((copy) => copy.remove());
        instances.delete(root);
      });
      resize();
    });
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-pv-review-carousel]').forEach((root) => instances.get(root)?.());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
